import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import { useToastStore } from '../../store/toastStore'
import { translateApiError } from '../../lib/apiErrors'
import AuthBrand from '../../components/auth/AuthBrand'
import AuthShell from '../../components/auth/AuthShell'
import {
  authHeadingClass,
  authInputClass,
  authLabelClass,
  authLinkClass,
  authSubtextClass,
  authSubmitClass,
} from '../../components/auth/authTheme'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const { t } = useTranslation(['auth', 'common'])
  const { forgotPassword, isLoading } = useAuthStore()
  const { addToast } = useToastStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      addToast(t('common:form.emailRequired'), 'error')
      return
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      addToast(t('common:form.emailInvalidFormat'), 'error')
      return
    }

    try {
      await forgotPassword(email)
      setIsSubmitted(true)
      addToast(t('auth:forgotPassword.success'), 'success')
    } catch (error) {
      addToast(translateApiError(error, t), 'error')
    }
  }

  if (isSubmitted) {
    return (
      <AuthShell>
        <div className="text-center">
          <AuthBrand />
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-900/40 border border-green-700">
            <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className={`mt-6 ${authHeadingClass}`}>{t('common:emailSent.heading')}</h2>
          <p className="mt-2 text-sm text-gray-400">
            {t('auth:forgotPassword.sent.body', { email })}
          </p>
          <p className="mt-4 text-sm text-gray-400">{t('auth:forgotPassword.sent.instruction')}</p>
          <div className="mt-6">
            <Link to="/auth/login" className={authLinkClass}>
              {t('common:backToLogin')}
            </Link>
          </div>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <div>
        <AuthBrand />
        <h2 className={authHeadingClass}>{t('auth:forgotPassword.heading')}</h2>
        <p className={authSubtextClass}>{t('auth:forgotPassword.description')}</p>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className={authLabelClass}>
            {t('common:form.emailLabel')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className={`mt-1 rounded-md ${authInputClass}`}
            placeholder={t('common:form.enterEmailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div>
          <button type="submit" disabled={isLoading} className={authSubmitClass}>
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0d0d0d]" />
            ) : (
              t('auth:forgotPassword.submitButton')
            )}
          </button>
        </div>

        <div className="text-center space-y-2">
          <Link to="/auth/login" className={authLinkClass}>
            {t('common:backToLogin')}
          </Link>
          <div className="text-sm text-gray-500">
            {t('auth:forgotPassword.noAccount')}{' '}
            <Link to="/auth/register" className={authLinkClass}>
              {t('auth:forgotPassword.registerLink')}
            </Link>
          </div>
        </div>
      </form>
    </AuthShell>
  )
}
